import { Link, useNavigate, useParams } from "react-router-dom";
import { getMovieById, getRelatedMovies, typeLabel } from "@/utils/movieUtils";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import FavoriteButton from "./components/FavoriteButton";

const metaRows = [
  { key: "type", label: "类型" },
  { key: "year", label: "年份" },
  { key: "region", label: "地区" },
  { key: "genres", label: "题材" },
];

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const movie = id ? getMovieById(id) : undefined;

  if (!movie) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="影视内容不存在或已下架"
          description="你访问的作品可能已被移除，或者链接有误。返回首页浏览更多精彩内容吧。"
          actionLabel="返回首页"
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  const related = getRelatedMovies(movie, 4);
  const metaValues: Record<string, string> = {
    type: typeLabel(movie.type),
    year: String(movie.year),
    region: movie.region,
    genres: movie.genres.join(" / "),
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: typeLabel(movie.type), to: `/category/${movie.type}` },
          { label: movie.title },
        ]}
      />

      <div className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="grid grid-cols-1 gap-6 p-5 md:grid-cols-[240px_1fr] md:gap-8 md:p-8">
          <div className="mx-auto w-[200px] overflow-hidden rounded-lg bg-background-200 md:mx-0 md:w-full">
            <img
              src={movie.cover}
              alt={`${movie.title} 封面`}
              title={`${movie.title} 日本影视`}
              className="aspect-[2/3] h-full w-full object-cover object-top"
            />
          </div>

          <div className="flex flex-col">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
                {typeLabel(movie.type)}
              </span>
              {movie.tags.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="rounded bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-800"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {movie.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1 font-semibold text-accent-600">
                <i className="ri-star-fill" aria-hidden="true"></i>
                {movie.rating.toFixed(1)}
              </span>
              <span className="text-background-400">|</span>
              <span>{movie.year} 年</span>
              <span className="text-background-400">|</span>
              <span>{movie.region}</span>
              {movie.episodes > 0 && (
                <>
                  <span className="text-background-400">|</span>
                  <span>共 {movie.episodes} 集</span>
                </>
              )}
            </div>

            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              {movie.synopsis}
            </p>

            <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
              {metaRows.map((row) => (
                <div
                  key={row.key}
                  className="flex items-center gap-3 border-b border-dashed border-background-200 py-1.5 text-sm"
                >
                  <dt className="w-12 shrink-0 text-foreground-400">
                    {row.label}
                  </dt>
                  <dd className="text-foreground-800">{metaValues[row.key]}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <FavoriteButton movieId={movie.id} />
              <Link
                to={`/category/${movie.type}`}
                className="flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                <i className="ri-grid-line" aria-hidden="true"></i>
                进入{typeLabel(movie.type)}频道
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
        <article className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className="ri-book-open-line text-lg" aria-hidden="true"></i>
            </span>
            剧情介绍
          </h2>
          <p className="mt-4 text-sm leading-7 text-foreground-600">
            {movie.description}
          </p>
          <p className="mt-4 text-sm leading-7 text-foreground-600">
            本片由 {movie.director} 执导，{movie.cast.join("、")} 等主演。作品以细腻的影像语言与扎实的叙事，呈现出独特的日式美学风格，是同类题材中值得关注的一部。
          </p>

          <h3 className="mt-6 font-heading text-base font-bold text-foreground-900">
            标签
          </h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {movie.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
              >
                {tag}
              </span>
            ))}
          </div>
        </article>

        <aside className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="font-heading text-base font-bold text-foreground-950">
            主创信息
          </h2>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="w-14 shrink-0 text-foreground-400">导演</span>
              <span className="text-foreground-800">{movie.director}</span>
            </li>
            <li className="flex gap-3">
              <span className="w-14 shrink-0 text-foreground-400">主演</span>
              <span className="text-foreground-800">{movie.cast.join("、")}</span>
            </li>
            <li className="flex gap-3">
              <span className="w-14 shrink-0 text-foreground-400">地区</span>
              <span className="text-foreground-800">{movie.region}</span>
            </li>
          </ul>
          <p className="mt-4 rounded-md bg-background-100 p-3 text-xs leading-relaxed text-foreground-500">
            本站仅提供影视简介与封面展示，不提供在线播放与下载功能。以上信息均为示范数据。
          </p>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className="ri-magic-line text-lg" aria-hidden="true"></i>
            </span>
            相关推荐
          </h2>
          <div className="grid grid-cols-2 gap-3 md:gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}